// frontend/src/stores/bookings.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from './auth'
import { useToastStore } from './toast.js'

export const useBookingsStore = defineStore('bookings', () => {
  // ========== STATE (все ref в начале) ==========
  const bookings = ref([])
  const allBookings = ref([])
  const availableDates = ref([])
  const selectedDate = ref('')
  const statusFilter = ref('all')
  const masterFilter = ref('all')   // 'all' или id мастера
  const isLoading = ref(false)

  // === ВХОДЯЩИЕ (pending) — объявляем ДО функций ===
  const pendingBookings = ref([])
  const pendingCount = ref(0)

  // ========== COMPUTED ==========
  const filteredBookings = computed(() => {
    if (statusFilter.value === 'all') return bookings.value
    return bookings.value.filter(b => b.status === statusFilter.value)
  })

  // Запись относится к мастеру, если он исполнитель записи или любой из её услуг
  const bookingHasMaster = (b, masterId) =>
    b.user_id === masterId || (Array.isArray(b.services) && b.services.some(s => s.master_id === masterId))

  // Записи после фильтров статуса и мастера — их показывают все виды (список, Kanban, расписание)
  const visibleBookings = computed(() => {
    const list = filteredBookings.value
    if (masterFilter.value === 'all') return list
    return list.filter(b => bookingHasMaster(b, masterFilter.value))
  })

  const totalRevenue = computed(() => {
    return bookings.value
      .filter(b => b.status !== 'cancelled' && b.status !== 'no_show')
      .reduce((sum, b) => sum + Number(b.total_price || b.price || 0), 0)
  })

  // ========== ACTIONS ==========
  async function loadForDate(date) {
    if (!date) return
    isLoading.value = true
    try {
      // Фильтры (статус, мастер) применяются на клиенте — иначе при переключении видов и смене
      // фильтра пришлось бы перезагружать день, а счётчики «N из M» теряли бы общее число записей.
      const url = `/bookings?date=${date}`
      const { data } = await api.get(url)
      const withDetails = await Promise.all(
        data.map(async (b) => {
          try {
            const [{ data: services }, { data: reminders }] = await Promise.all([
              api.get(`/bookings/${b.id}/services`),
              api.get(`/bookings/${b.id}/reminders`)
            ])
            return { ...b, services, reminders }
          } catch {
            return b
          }
        })
      )
      bookings.value = withDetails
      selectedDate.value = date
    } catch (err) {
      console.error('Ошибка загрузки записей:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function loadMonthBookings(month) {
    const [year, mon] = month.split('-').map(Number)
    const daysInMonth = new Date(year, mon, 0).getDate()
    const dates = Array.from({ length: daysInMonth }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`)
    const all = []
    // раньше 28–31 запрос шли строго по очереди — теперь пачками по 8 параллельно
    const BATCH = 8
    for (let i = 0; i < dates.length; i += BATCH) {
      const chunk = await Promise.all(
        dates.slice(i, i + BATCH).map(d => api.get(`/bookings?date=${d}`).then(r => r.data || []).catch(() => []))
      )
      chunk.forEach(rows => all.push(...rows))
    }
    allBookings.value = all
  }

  async function updateStatus(id, status) {
    await api.put(`/bookings/${id}/status`, { status })
    await loadForDate(selectedDate.value)
  }

  // ========== KANBAN ==========
async function updateStatusKanban(bookingId, newStatus, reason = '', options = {}) {
  const toastStore = useToastStore()
  const booking = bookings.value.find(b => b.id === bookingId)
  if (!booking) return

  const oldStatus = booking.status
  
  // Optimistic update
  booking.status = newStatus
  
  try {
    const response = await api.patch(`/bookings/${bookingId}/status`, {
      status: newStatus,
      reason,
      ...(options.undo ? { undo: true } : {})
    })
    
    // Update with server response
    const index = bookings.value.findIndex(b => b.id === bookingId)
    if (index !== -1) {
      // сохраняем поля, которых нет в ответе (имя клиента, услуги, напоминания)
      bookings.value[index] = { ...bookings.value[index], ...response.data }
    }
    
    // Toast success
    const statusLabels = {
      'pending': 'Ожидает подтверждения',
      'confirmed': 'Подтверждена',
      'arrived': 'Клиент пришёл',
      'in_progress': 'В работе',
      'completed': 'Завершена',
      'cancelled': 'Отменена',
      'no_show': 'Не пришёл'
    }
    
    // Плашка с кнопкой «Отменить» (в Dashboard) заменяет тост, когда действие можно откатить
    if (!options.silent) {
      toastStore.success(
        `${statusLabels[oldStatus] || oldStatus} → ${statusLabels[newStatus] || newStatus}`,
        'Статус обновлён'
      )
    }
    
    return response.data
    
  } catch (err) {
    // Rollback
    const index = bookings.value.findIndex(b => b.id === bookingId)
    if (index !== -1) {
      bookings.value[index].status = oldStatus
    }
    
    // Toast error
    const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Не удалось изменить статус'
    toastStore.error(errorMsg, 'Ошибка')
    
    // Не throw — toast уже показал ошибку
    console.error('Status update failed:', err)
  }
}

  // ========== ВХОДЯЩИЕ (pending) ==========
  async function loadPending() {
    isLoading.value = true
    try {
      const { data } = await api.get('/bookings/pending')

      // Загружаем детали для каждой входящей записи (services + reminders)
      const withDetails = await Promise.all(
        (data || []).map(async (b) => {
          try {
            const [{ data: services }, { data: reminders }] = await Promise.all([
              api.get(`/bookings/${b.id}/services`),
              api.get(`/bookings/${b.id}/reminders`)
            ])
            return { ...b, services, reminders }
          } catch (err) {
            console.warn(`Не удалось загрузить детали для записи ${b.id}:`, err.message)
            return b
          }
        })
      )

      pendingBookings.value = withDetails
      pendingCount.value = withDetails.length
    } catch (err) {
      console.error('Ошибка загрузки входящих:', err)
      pendingBookings.value = []
      pendingCount.value = 0
    } finally {
      isLoading.value = false
    }
  }

  async function loadPendingCount() {
    try {
      const { data } = await api.get('/bookings/pending/count')
      pendingCount.value = data.count || 0
    } catch (err) {
      console.error('Ошибка загрузки счётчика:', err)
    }
  }

  async function confirmBooking(id) {
    await api.patch(`/bookings/${id}/status`, { status: 'confirmed' })
    pendingBookings.value = pendingBookings.value.filter(b => b.id !== id)
    pendingCount.value = Math.max(0, pendingCount.value - 1)
  }

  async function rejectBooking(id, reason) {
    await api.patch(`/bookings/${id}/status`, { 
      status: 'cancelled', 
      reason: reason || 'Отклонено администратором' 
    })
    pendingBookings.value = pendingBookings.value.filter(b => b.id !== id)
    pendingCount.value = Math.max(0, pendingCount.value - 1)
  }

  async function loadAvailableDates(masterId, month) {
    if (!masterId) return
    try {
      const { data } = await api.get(`/slots/dates?user_id=${masterId}&month=${month}`)
      availableDates.value = data || []
    } catch {
      availableDates.value = []
    }
  }

  // Доп. услуга в текущей записи (цена добавляется к сумме, время — по желанию продлевает запись)
  async function addService(bookingId, payload) {
    const { data } = await api.post(`/bookings/${bookingId}/services`, payload)
    return data
  }

  // Откат последней добавленной услуги
  async function removeService(bookingId, rowId) {
    const { data } = await api.delete(`/bookings/${bookingId}/services/${rowId}`)
    return data
  }

  // Растягивание стикера: меняется только длительность (сумма записи остаётся прежней)
  async function updateDuration(id, durationMinutes) {
    const { data } = await api.patch(`/bookings/${id}/duration`, { duration_minutes: durationMinutes })
    return data
  }

  function $reset() {
    bookings.value = []
    allBookings.value = []
    availableDates.value = []
    selectedDate.value = ''
    statusFilter.value = 'all'
    masterFilter.value = 'all'
    pendingBookings.value = []
    pendingCount.value = 0
  }

  return {
    bookings, allBookings, availableDates, selectedDate, statusFilter, masterFilter,
    isLoading, filteredBookings, visibleBookings, bookingHasMaster, totalRevenue,
    // === ВХОДЯЩИЕ ===
    pendingBookings, pendingCount,
    // ===
    loadForDate, loadMonthBookings, updateDuration, addService, removeService, updateStatus, updateStatusKanban, 
    loadPending, loadPendingCount, confirmBooking, rejectBooking, 
    loadAvailableDates, $reset
  }
})